import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { getAvatarUrl } from '../utils/avatar';
import { Eye, EyeOff } from 'lucide-react';
import PasswordStrengthGuide, { getPasswordValidation } from '../Components/common/PasswordStrengthGuide';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar, fullProfile?.avatar]);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    }
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });


  const fetchReferralData = async () => {
    const userId = user?._id || user?.id;
    if (!userId) return;
    setReferralLoading(true);
    try {
      const response = await api.get(`/users/${userId}/referral`);
      setReferralData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!referralData?.referralUrl) return;
    navigator.clipboard.writeText(referralData.referralUrl)
      .then(() => {
        setCopied(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error('Failed to copy'));
  };

  const handleDownloadQRCode = () => {
    if (!referralData?.qrCodeDataUrl) return;
    const link = document.createElement('a');
    link.href = referralData.qrCodeDataUrl;
    link.download = `referral-qrcode-${referralData.referralCode || 'code'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code download started!');
  };
  const fetchFullProfile = async () => {
    try {
      const response = await api.get('/auth/profile');
      const profileData = response.data.data;
      setFullProfile(profileData);

      // Determine the best avatar: database field OR the uploaded onboarding photo document
      const photoDoc = (profileData?.onboardingDocuments || []).find(doc => doc.type === 'PHOTO');
      let resolvedAvatar = profileData?.avatar || photoDoc?.url;
      if (resolvedAvatar) resolvedAvatar = resolvedAvatar.replace(/\\/g, '/');

      // Determine the best address: direct profile address OR the onboarding address data
      const resolvedAddress = profileData?.address || {
        street: profileData?.onboardingData?.currentAddress || '',
        city: profileData?.onboardingData?.city || '',
        state: profileData?.onboardingData?.state || '',
        pincode: profileData?.onboardingData?.pincode || '',
        country: 'India'
      };

      const hasAvatarChange = resolvedAvatar && resolvedAvatar !== user?.avatar;
      const hasAddressChange = resolvedAddress && JSON.stringify(resolvedAddress) !== JSON.stringify(user?.address);

      if (hasAvatarChange || hasAddressChange) {
        updateUser({
          ...user,
          avatar: resolvedAvatar || user?.avatar,
          address: resolvedAddress || user?.address
        });
      }
    } catch (error) {
      console.error('Failed to fetch full profile:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchFullProfile();
      fetchReferralData();
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        address: {
          street: user.address?.street || '',
          city: user.address?.city || '',
          state: user.address?.state || '',
          pincode: user.address?.pincode || ''
        }
      });
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put('/auth/profile', formData);
      updateUser(response.data.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    const validation = getPasswordValidation(passwordData.newPassword);
    if (!validation.isValid) {
      toast.error(validation.firstError || 'New password does not meet security requirements');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      customer_care: 'bg-blue-100 text-blue-800',
      cc_head: 'bg-blue-100 text-blue-800',
      doctor: 'bg-green-100 text-green-800',
      hospital: 'bg-purple-100 text-purple-800',
      marketing_agent: 'bg-yellow-100 text-yellow-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <button
          onClick={() => setShowPasswordModal(true)}
          className="px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
        >
          Change Password
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    disabled={user?.role !== 'admin'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${user?.role !== 'admin' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    disabled={user?.role !== 'admin'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${user?.role !== 'admin' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    maxLength={10}
                    disabled={user?.role !== 'admin'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${user?.role !== 'admin' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="text-md font-medium text-gray-900 mb-4">Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street</label>
                    <input
                      type="text"
                      value={formData.address.street}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: { ...formData.address, street: e.target.value }
                        })
                      }
                      disabled={user?.role !== 'admin'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${user?.role !== 'admin' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: { ...formData.address, city: e.target.value }
                        })
                      }
                      disabled={user?.role !== 'admin'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${user?.role !== 'admin' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={formData.address.state}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: { ...formData.address, state: e.target.value }
                        })
                      }
                      disabled={user?.role !== 'admin'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${user?.role !== 'admin' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                    <input
                      type="text"
                      value={formData.address.pincode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          address: { ...formData.address, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }
                        })
                      }
                      disabled={user?.role !== 'admin'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${user?.role !== 'admin' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                      maxLength={6}
                    />
                  </div>
                </div>
              </div>

              {user?.role === 'admin' && (
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Professional Details Section (Conditional) */}
          {(fullProfile?.doctorProfile || fullProfile?.hospitalProfile) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {user?.role === 'doctor' ? 'Professional Credentials' : 'Facility Information'}
              </h2>
              
              {user?.role === 'doctor' && fullProfile.doctorProfile && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Specialization</p>
                    <p className="text-sm font-medium text-gray-900">
                      {fullProfile.doctorProfile.specializationBranch 
                        ? `${fullProfile.doctorProfile.specializationBranch} (${fullProfile.doctorProfile.specialization})` 
                        : fullProfile.doctorProfile.specialization || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Registration Number</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.doctorProfile.registrationNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Doctor Type</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.doctorProfile.doctorType || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Working Hospital</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.doctorProfile.workingHospital || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Experience</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.doctorProfile.totalExperience || fullProfile.doctorProfile.experience || 0} Years</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Verification Status</p>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      fullProfile.doctorProfile.verificationStatus === 'verified' ? 'bg-green-100 text-green-700' : 
                      fullProfile.doctorProfile.verificationStatus === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {fullProfile.doctorProfile.verificationStatus?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                </div>
              )}

              {user?.role === 'hospital' && fullProfile.hospitalProfile && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Hospital Name</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.hospitalProfile.hospitalName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Registration ID</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.hospitalProfile.registrationNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Hospital Type</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.hospitalProfile.hospitalType || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Contact Person</p>
                    <p className="text-sm font-medium text-gray-900">{fullProfile.hospitalProfile.contactPerson?.name || 'N/A'}</p>
                  </div>
                </div>
              )}

              {/* Documents Subsection */}
              <div className="mt-8 border-t pt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="C9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Uploaded Documents
                </h3>
                
                {((user?.role === 'doctor' && fullProfile.doctorProfile?.documents?.length > 0) || 
                  (user?.role === 'hospital' && fullProfile.hospitalProfile?.documents?.length > 0)) ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(user?.role === 'doctor' ? fullProfile.doctorProfile.documents : fullProfile.hospitalProfile.documents).map((doc, idx) => (
                      <a 
                        key={idx}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors group"
                      >
                        <div className="bg-primary-50 p-2 rounded-md mr-3 group-hover:bg-primary-100 transition-colors">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{doc.name || doc.type?.replace('_', ' ')}</p>
                          <p className="text-xs text-gray-500">Uploaded on {new Date(doc.uploadedAt).toLocaleDateString()}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <p className="text-sm text-gray-500 italic">No verification documents submitted yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full bg-primary-50 flex items-center justify-center overflow-hidden border-4 border-white shadow-xl ring-1 ring-slate-100">
                    {(() => {
                      const photoDoc = (fullProfile?.onboardingDocuments || []).find(doc => doc.type === 'PHOTO');
                      const avatarUrl = fullProfile?.avatar || user?.avatar || photoDoc?.url;
                      if (avatarUrl && !avatarError) {
                        return (
                          <img 
                            key={avatarUrl}
                            src={getAvatarUrl(avatarUrl)} 
                            alt="" 
                            className="w-full h-full object-cover" 
                            onError={() => setAvatarError(true)}
                          />
                        );
                      }
                      return (
                        <span className="text-3xl font-black text-primary-600">
                          {user?.firstName?.charAt(0)}
                          {user?.lastName?.charAt(0)}
                        </span>
                      );
                    })()}
                  </div>
                  
                  <>
                    <label 
                      htmlFor="avatar-upload" 
                      className="absolute inset-0 flex items-center justify-center bg-black/40 text-white rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-all duration-300 backdrop-blur-[2px]"
                    >
                      <div className="flex flex-col items-center">
                        <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-[8px] font-black uppercase tracking-widest">Edit</span>
                      </div>
                    </label>
                    <input 
                      type="file" 
                      id="avatar-upload" 
                      className="hidden" 
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('avatar', file);
                        
                        const uploadToast = toast.loading('Uploading photo...');
                        try {
                          const response = await api.put('/auth/avatar', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          updateUser(response.data.data);
                          toast.success('Photo updated!', { id: uploadToast });
                        } catch (err) {
                          toast.error(err.response?.data?.message || 'Upload failed', { id: uploadToast });
                        }
                      }}
                    />
                  </>
                </div>
              </div>
              <div className="text-center">
                <p className="text-xl font-black text-[#143E78] tracking-tight">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs font-bold text-gray-400 mt-1">{user?.email}</p>
              </div>
              <div className="flex justify-center">
                <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm ${getRoleColor(user?.role)}`}>
                  {user?.role === 'marketing_agent' ? 'Lead Partner' : user?.role?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h2>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Active</p>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${user?.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                >
                  {user?.isActive ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Verified</p>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${user?.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}
                >
                  {user?.isVerified ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Member Since</p>
                <p className="text-sm font-medium">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* ─── Referral Link Card ─── */}
          <div className="bg-gradient-to-br from-[#143E78] to-[#1a5099] rounded-3xl shadow-2xl shadow-blue-900/30 p-6 text-white overflow-hidden relative">
            {/* Decorative glow */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-blue-400/10 rounded-full blur-xl" />

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-white/10 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-widest text-white/80">My Referral Link</h3>
                  <p className="text-[9px] text-blue-200/70 font-bold uppercase tracking-wider">Share & Track</p>
                </div>
              </div>

              {referralLoading ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-32 w-32 mx-auto bg-white/10 rounded-2xl" />
                  <div className="h-4 bg-white/10 rounded-lg" />
                  <div className="h-8 bg-white/10 rounded-xl" />
                </div>
              ) : referralData ? (
                <div className="space-y-4">
                  {/* QR Code */}
                  <div className="flex justify-center">
                    <div className="bg-white p-3 rounded-2xl shadow-xl">
                      <img
                        src={referralData.qrCodeDataUrl}
                        alt="Referral QR Code"
                        className="w-28 h-28 rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Referral Code */}
                  <div className="text-center">
                    <p className="text-[9px] font-black text-blue-200/70 uppercase tracking-widest mb-1">Your Referral Code</p>
                    <span className="inline-block bg-white/15 border border-white/20 px-4 py-1.5 rounded-full text-sm font-black tracking-widest text-white">
                      {referralData.referralCode}
                    </span>
                  </div>

                  {/* Referral URL */}
                  <div className="bg-white/10 border border-white/15 rounded-2xl p-3">
                    <p className="text-[9px] font-black text-blue-200/60 uppercase tracking-widest mb-1">Share this link</p>
                    <p className="text-[10px] text-blue-100 font-bold break-all leading-relaxed">
                      {referralData.referralUrl}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopyLink}
                      className={`flex-1 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 ${
                        copied
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/40'
                          : 'bg-white text-[#143E78] hover:bg-blue-50 shadow-lg shadow-black/20'
                      }`}
                    >
                      {copied ? '✓ Copied!' : 'Copy Link'}
                    </button>
                    <button
                      onClick={handleDownloadQRCode}
                      className="flex-1 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 bg-white/10 hover:bg-white/20 text-white border border-white/20 shadow-lg shadow-black/10 flex items-center justify-center gap-1.5"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                  </div>

                  {/* Referral Stats */}
                  <div className="flex items-center justify-between bg-white/10 rounded-2xl px-4 py-3 border border-white/10">
                    <div>
                      <p className="text-[9px] font-black text-blue-200/70 uppercase tracking-widest">Total Referrals</p>
                      <p className="text-2xl font-black text-white mt-0.5">{referralData.referralCount}</p>
                    </div>
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>

                  <p className="text-center text-[9px] text-blue-200/50 font-bold leading-relaxed">
                    Share this QR code or link. When someone books via your link, it gets tracked to you.
                  </p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-blue-200/60 text-xs font-bold">Could not load referral data</p>
                  <button onClick={fetchReferralData} className="mt-2 text-xs text-white font-black underline">Retry</button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Change Password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                    className="w-full px-3.5 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    className="w-full px-3.5 py-2.5 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {/* Live Password Checklist */}
                <PasswordStrengthGuide password={passwordData.newPassword} />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    className={`w-full px-3.5 py-2.5 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium text-sm ${
                      passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                        ? 'border-rose-400 bg-rose-50/20'
                        : passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword
                        ? 'border-emerald-400 bg-emerald-50/20'
                        : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 font-bold text-sm text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !getPasswordValidation(passwordData.newPassword).isValid}
                  className="px-5 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 font-bold text-sm shadow-md transition-all"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
